/**
 * Ksting
 *
 * Copyright Â© 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, { Component } from 'react';
import { string, bool, func } from 'prop-types';
import classNames from 'clsx';
import DatePicker from 'react-datepicker';
import { format, parse, isValid } from 'date-fns';
import orderBy from 'lodash/orderBy';
import uniqBy from 'lodash/uniqBy';

import p from 'polyglot/polyglot';

import Button from 'components/Button/Button';
import CleaveInput from 'components/Form/Input/CleaveInput/CleaveInput';

import CustomInput from './CustomInput/CustomInput';

import 'react-datepicker/dist/react-datepicker-cssmodules.css';
import styles from './date-picker-input.scss';

/**
 * This is the DatePickerInput component.
 *
 * @author Chris Andersson <chris@wbisbi.com>
 */
export default class DatePickerInput extends Component {
  /**
   * Declare expected prop types.
   *
   * @type {object}
   */
  static propTypes = {
    name: string.isRequired,
    format: string,
    placeholder: string,
    value: string,
    required: bool,
    disabled: bool,
    multiple: bool,
    readOnly: bool,
    error: bool,
    disableTimeRange: bool,
    onFocus: func,
    onBlur: func,
    onChange: func,
  };

  static defaultProps = {
    format: 'yyyy-MM-dd',
    placeholder: '',
    value: '',
    required: false,
    error: false,
    disableTimeRange: false,
    disabled: false,
    multiple: false,
    readOnly: false,
    large: false,
    compact: false,
    onFocus: () => {},
    onBlur: () => {},
    onChange: () => {},
  };

  constructor(props) {
    super(props);

    this.datepicker = React.createRef();

    this.state = {
      selectedDate: null,
      timeStarts: '',
      timeEnds: '',
    };
  }

  getFirstDate = () => {
    const { value: initialValue, format: dateFormat } = this.props;

    // Create array from string.
    const dates = initialValue ? initialValue.split(',') : null;

    const date =
      dates && dates.length > 0 && dates[0]
        ? parse(dates[0], dateFormat, new Date())
        : null;

    // Return first date from array.
    return isValid(date) ? date : null;
  };

  getDates = () => {
    const { value: initialValue, format: dateFormat } = this.props;

    if (initialValue) {
      const dates = initialValue.split(',');

      return dates.filter(date => {
        const disableTimeRange = date.split(' ')[0]; // remove time.
        const parsedDate = parse(disableTimeRange, dateFormat, new Date());

        return isValid(parsedDate);
      });
    }

    return [];
  };

  getHighlightDates = () => {
    const { value: initialValue, format: dateFormat } = this.props;

    // Create array from string.
    if (initialValue) {
      const dates = initialValue.split(',');

      return dates.map(date => {
        const disableTimeRange = date.split(' ')[0]; // remove time.

        return parse(disableTimeRange, dateFormat, new Date());
      });
    }

    return [];
  };

  onKeyPress = e => e.key === 'Enter' && e.preventDefault();

  onSelect = selectedDate => {
    const { disableTimeRange } = this.props;

    this.setState({ selectedDate }, () => {
      if (disableTimeRange) {
        this.triggerChange();
      }
    });
  };

  onClickOutside = () => {
    // Reset times.
    this.setState({
      selectedDate: null,
      timeStarts: '',
      timeEnds: '',
    });
  };

  onClickSubmit = event => {
    event.preventDefault();

    this.triggerChange();
  };

  onRemove = date => {
    const { value: initialValue, onChange } = this.props;

    let dates = initialValue.split(',');
    dates = dates.filter(d => d !== date);

    const datesList = dates.length > 0 ? dates.join(',') : null;

    // We manipulute the onChangeHandler
    // in Input so it thinks it gets an input.
    onChange({ target: { value: datesList } });
  };

  onChangeTimeStarts = event => {
    const timeStarts = event.target.value;

    this.setState({ timeStarts });
  };

  onChangeTimeEnds = event => {
    const timeEnds = event.target.value;

    this.setState({ timeEnds });
  };

  triggerChange = () => {
    const { selectedDate: date } = this.state;

    if (!date) {
      this.datepicker.current.setOpen(false);
      return;
    }

    const {
      multiple,
      value: initialValue,
      format: dateFormat,
      onChange,
    } = this.props;

    const { timeStarts, timeEnds } = this.state;

    let value = date ? format(date, dateFormat) : null;

    // Add time if there is any.
    if (timeStarts) {
      let dateWithTime = `${value} ${timeStarts}`;

      if (timeEnds) {
        dateWithTime = `${value} ${timeStarts}-${timeEnds}`;
      }

      value = dateWithTime;
    }

    if (multiple) {
      let dates = initialValue ? initialValue.split(',') : [];
      // Add item as first in array for unique to work.
      dates.unshift(value);

      // Remove duplicates.
      const uniqueDates = uniqBy(dates, d => {
        return d.split(' ')[0]; // remove time.
      });

      dates = orderBy(uniqueDates);

      const datesList = dates.join(',');

      value = datesList;
    }

    // Reset.
    this.setState({
      selectedDate: null,
      timeStarts: '',
      timeEnds: '',
    });

    this.datepicker.current.setOpen(false);

    // We manipulute the onChangeHandler
    // in Input so it thinks it gets an input.
    onChange({ target: { value } });
  };

  renderCustomInput = () => {
    const { multiple } = this.props;

    if (multiple) {
      return <CustomInput items={this.getDates()} onRemove={this.onRemove} />;
    }

    return null;
  };

  /**
   * Render DatePickerInput.
   *
   * @return {Object}
   */
  render() {
    const {
      name,
      format,
      placeholder,
      error,
      required,
      disabled,
      readOnly,
      disableTimeRange,
    } = this.props;

    const { selectedDate, timeStarts, timeEnds } = this.state;

    return (
      <DatePicker
        ref={this.datepicker}
        className={classNames(styles.input, {
          [styles.error]: error,
        })}
        selected={selectedDate}
        dateFormat={format}
        minDate={new Date()}
        placeholderText={placeholder}
        highlightDates={this.getHighlightDates()}
        customInput={this.renderCustomInput()}
        shouldCloseOnSelect={disableTimeRange ? true : false}
        onChange={this.onSelect}
        onClickOutside={this.onClickOutside}
        {...{
          name,
          required,
          disabled,
          readOnly,
        }}
      >
        {!disableTimeRange && (
          <div className={styles.blocks}>
            <div className={styles.block}>
              <p className={styles.label}>
                {p.t('general.time_starts')}
                <em>{p.t('general.form.optional')}</em>
              </p>
              <CleaveInput
                className={styles.time}
                name="time-starts"
                value={timeStarts}
                onChange={this.onChangeTimeStarts}
                onKeyPress={this.onKeyPress}
                options={{
                  time: true,
                  timePattern: ['h', 'm'],
                }}
              />
            </div>
            <div className={styles.block}>
              <p className={styles.label}>
                {p.t('general.time_ends')}
                <em>{p.t('general.form.optional')}</em>
              </p>
              <CleaveInput
                className={styles.time}
                name="time-ends"
                value={timeEnds}
                onChange={this.onChangeTimeEnds}
                onKeyPress={this.onKeyPress}
                options={{
                  time: true,
                  timePattern: ['h', 'm'],
                }}
              />
            </div>

            <div className={styles.button}>
              <Button small onClick={this.onClickSubmit}>
                {p.t('general.form.submit')}
              </Button>
            </div>
          </div>
        )}
      </DatePicker>
    );
  }
}
